import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Firestore } from '@angular/fire/firestore';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { AuthStateService } from '@core/auth-state.service';
import { CatchErrorService } from '@core/catch-error.service';
import { doc, DocumentReference, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BehaviorSubject, catchError, defer, delayWhen, filter, first, from, map, Observable, of, race, retryWhen, scan, shareReplay, Subject, switchMap, take, takeUntil, tap, throwError, timeout, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private uid$?: Observable<any | null>;
  private destroy$ = new Subject<void>();
  private slideSource = new BehaviorSubject<Data>(null);
  slide$ = this.slideSource.asObservable();
  constructor(
    private injector: Injector,
    private catchService: CatchErrorService,
    private firestore: Firestore,
    private angularFireAuth: AngularFireAuth,
    private authStateService: AuthStateService,
  ) { }

  fallbackMessages = {
    "1026": {
      message: {
        line_1: "Something went wrong. Please try again",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    },
    "1083": {
      message: {
        line_1: "Your web browser does not support storing settings locally. In Safari, the most common cause of this is using 'Private Browsing Mode'. Some settings may not save or some features may not work properly for you.",
        line_2: "",
      },
      style: {
        icon: "tick circle alert.svg",
        type: "warning",
      }
    },
    "1113": {
      message: {
        line_1: "The password provided does not match",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    },
    "1125": {
      message: {
        line_1: "You have attempted too many times. Please try again after some time.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    },
    "1126": {
      message: {
        line_1: "This account has been permanently closed. Please contact support@lottosocial.com if you have any questions",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      }
    }
  };


  private getFallbackMessage(msgCode: string): Data {
    return this.fallbackMessages[msgCode] || null;
  }


  getAngularFireStore(url: string, method: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return of({ data: () => null, exists: () => false }); // Handle offline case

        const docRef: DocumentReference = doc(this.firestore, url);
        const needsAuthGate = typeof url === 'string' && url.includes('users/');

        const runOp = () => {
          if (method === "get") {
            return from(getDoc(docRef)); // 🔥 FIX: wrap in `from()` to return Observable
          }

          if (method === "snapshotChanges") {
            return new Observable((observer) => {
              const unsubscribe = onSnapshot(
                docRef,
                (docSnapshot) => observer.next(docSnapshot),
                (error) => observer.error(error)
              );
              return () => unsubscribe();
            }).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          }

          return of(null); // Default fallback
        };

        // Gate user-private paths only (LOTTOSOCIAL-PWA-1MEE); public paths unchanged
        return needsAuthGate
          ? this.authStateService.customTokenReady$.pipe(take(1), switchMap(() => runOp()))
          : runOp();
      }),
      catchError((error) => {
        this.catchService.catchOfflineError(of(error));
        throw error;
      }),
      shareReplay(1)
    );
  }
  // Offline queue
  private offlineQueue: Array<{
    name: string;
    payload: Data | string;
    options: Data;
    resolver: { resolve: Function; reject: Function };
  }> = [];


  /**
   * Hard ceiling on each of the three auth waits in getUid() (token refresh,
   * Auth init / currentUser, and customTokenReady$). A token refresh or an Auth
   * init that never settles must not be able to hold a caller open for an
   * unbounded time. Worst case the three gates can take roughly
   * 3 * AUTH_GATE_MAX_WAIT_MS.
   *
   * This is a safety net, not a latency target. Keeping it generous means it
   * rarely hands out a premature null — which is what would reintroduce the
   * permission-denied races these waits were added for (LOTTOSOCIAL-PWA-1MED /
   * 1MEE). Callers that genuinely cannot afford to wait should cap their own
   * wait instead; see AuthPartialRegService.partial_Reg.
   */
  private static readonly AUTH_GATE_MAX_WAIT_MS = 5000;

  getUid(caller: string): Observable<string | null> {
    // Wait for any in-flight token refresh to complete before resolving the uid.
    // During a refresh cycle angularFireAuth.currentUser can transiently return
    // null, which would cause every downstream Firestore operation to receive a
    // null uid and trigger permission-denied errors.
    // Also wait for customTokenReady$ when a user exists so downstream Firestore
    // reads do not race the iOS/Safari custom-token round-trip (LOTTOSOCIAL-PWA-1MEE).
    // Logged-out callers still resolve null immediately (no wait on the token signal).
    // Fallback: if customTokenReady$ never emits, resolve uid after
    // AUTH_GATE_MAX_WAIT_MS for best-effort callers. Firestore listeners must
    // still gate on customTokenReady$ themselves; this timeout is not auth-ready.
    // All three waits below are bounded. tokenRefreshInProgress$ is only cleared by
    // finishTokenRefreshAttempt(), so a custom-token round-trip that stalls used
    // to block this observable indefinitely; and angularFireAuth.currentUser does
    // not settle until Firebase Auth initialises, which can stall on iOS Safari
    // when IndexedDB/storage is unavailable. Either one previously stopped the
    // caller from ever issuing its request — including the registration route
    // resolver, which then sat on a loading overlay until it timed out.
    return race(
      this.authStateService.tokenRefreshInProgress$.pipe(
        filter(inProgress => !inProgress),
        first()
      ),
      timer(CommonService.AUTH_GATE_MAX_WAIT_MS).pipe(
        tap(() => this.reportAuthGateTimeout(caller, 'token_refresh'))
      )
    ).pipe(
      switchMap(() =>
        defer(() =>
          from(this.angularFireAuth.currentUser).pipe(
            timeout({
              each: CommonService.AUTH_GATE_MAX_WAIT_MS,
              with: () => {
                this.reportAuthGateTimeout(caller, 'auth_init');
                return of(null);
              },
            }),
            switchMap(user => {
              const uid = user?.uid ?? null;
              if (!uid) {
                return of(null);
              }
              return race(
                this.authStateService.customTokenReady$.pipe(take(1)),
                timer(CommonService.AUTH_GATE_MAX_WAIT_MS).pipe(
                  tap(() => this.reportAuthGateTimeout(caller, 'custom_token_ready'))
                )
              ).pipe(map(() => uid));
            }),
            catchError((err) => {
              this.reportAuthGateTimeout(caller, 'auth_init');
              this.logAuthGateError(err);
              return of(null);
            })
          )
        )
      ),
      catchError((err) => {
        this.logAuthGateError(err);
        return of(null);
      })
    );
  }


  /**
   * Fires when one of the auth gates above hits its ceiling and the caller is
   * handed a best-effort result instead of a waited-for one. Rare by design: a
   * cluster of these is the early warning that a permission-denied regression
   * is coming, and `caller` names the call site that is exposed.
   *
   * AuthService is resolved lazily so this helper cannot form a constructor
   * cycle (AuthService already injects CommonService). Logging must never
   * throw back into getUid().
   */
  private reportAuthGateTimeout(
    caller: string,
    stage: 'token_refresh' | 'auth_init' | 'custom_token_ready'
  ): void {
    try {
      this.injector.get(AuthService).errorHandlerLogs('[getUid] auth gate timed out', {
        caller: caller || 'unknown',
        stage,
        waitMs: CommonService.AUTH_GATE_MAX_WAIT_MS,
      });
    } catch {
      // Never let reporting break the auth gate fallback.
    }
  }

  private logAuthGateError(error: unknown): void {
    try {
      this.injector.get(AuthService).errorHandlerLogs('[getUid] unexpected error', error);
    } catch {
      // Never let reporting break the auth gate fallback.
    }
  }

  async getUserId(caller: string): Promise<string | null> {
    try {
      const user = await this.angularFireAuth.currentUser;
      if (!user) {
        return null;
      }
      return user?.uid ?? null;
    } catch (error) {
      return error;
    }
  }



  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}): Observable<any> {
    if (!navigator.onLine) {
      return new Observable(observer => {
        const resolver = {
          resolve: (result: Data) => observer.next(result),
          reject: (error: Data) => observer.error(error)
        };
        this.offlineQueue.push({ name: CloudFunctionName, payload, options, resolver });
        // Process offline queue when online
        window.addEventListener('online', () => {
          this.processOfflineQueue();
        }, { once: true });
      });
    }

    try {
      if (!navigator.onLine) {
        return throwError(() => new Error('OFFLINE_ERROR'));
      }

      const region = AuthService.FIREBASE_REGION || 'us-central1';
      const callable = httpsCallable(getFunctions(undefined, region), CloudFunctionName, options);


      return defer(() => from(callable(payload))).pipe(takeUntil(this.destroy$),
        tap(),

        map((res: Data) => {
          return res?.data;
        }),

        retryWhen((errors) =>
          errors.pipe(takeUntil(this.destroy$),
            tap((error) => {
              const msg = error?.message?.toLowerCase?.() || '';
              const isNetworkError = msg.includes('offline') || msg.includes('network');

              if (isNetworkError && !navigator.onLine) {
              }
            }),

            scan((retryCount, error) => {
              if (retryCount >= 3) {
                console.error(`Max retries reached for ${CloudFunctionName}`);
                throw error;
              }

              console.warn(`Retrying ${CloudFunctionName} attempt ${retryCount + 1}`);
              return retryCount + 1;
            }, 0),
            delayWhen(retryCount => timer(Math.min(Math.pow(2, retryCount) * 1000, 30000))) // Exponential backoff capped at 30s
          )
        ),
        catchError(error => {
          if (error?.message === 'OFFLINE_ERROR') {
            return throwError(() => new Error('Network offline. Please check your connection.'));
          }

          if (error?.message === 'UNAUTHENTICATED_ERROR') {
            return throwError(() => new Error('User authentication lost. Please log in again.'));
          }

          console.error(`Cloud function ${CloudFunctionName} failed:`, error);
          return throwError(() => new Error(`Error calling ${CloudFunctionName}: ${error?.message || error}`));
        })
      );
    } catch (error) {
      console.error(`Unexpected error in getV2CloudFunction:`, error);
      return throwError(() => new Error(`Unexpected error: ${error?.message || error}`));
    }
  }

  //Offline queue
  private processOfflineQueue() {
    if (!navigator.onLine) return;

    while (this.offlineQueue.length) {
      const { name, payload, options, resolver } = this.offlineQueue.shift()!;
      this.getV2CloudFunction(name, payload, options).subscribe({
        next: (result) => resolver.resolve(result),
        error: (error) => resolver.reject(error)
      });
    }
  }

  // fetch the slide data form store page and send to add card component 
  setSlideData(slide: Data) {
    console.log("the slide are", slide);
    this.slideSource.next(slide);
  }

  getSlideData(): Data {
    return this.slideSource.getValue();
  }

}
